import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';

export const usePopulateProcedureEffect = ({
    hasPopulatedProcedure,
    usageDetails,
    procedureInfoData,
    levelDataModified,
    modifyDraftProcedureDataResponse,
    dispatch,
    setRetractorUsedOtherField,
    mazorCaseCoverageEmployees,
    setMazorCaseCoverageValue,
    setSelectedMazorCaseCoverage,
    setMazorCaseCoverageInputValue,
    setStealthCaseCoverageValue,
    setSelectedStealthAxisCaseCoverage,
    setStealthAxisCaseCoverageInputValue,
    setUivDropdownValue,
    setLivDropdownValue,
    setProcedureInfoData,
    setProcedureData,
    reportUsageFormObj,
    setReportUsageFormObj,
    setHasPopulatedProcedure,
    addProcedureLevel,
    createProcedureDraftOrderObject,
    uivArray,
    livArray,
    totalLevelsNumber,
}) => {
    useEffectWithErrorLogging(() => {
        if (
            !hasPopulatedProcedure &&
            usageDetails?.procedure &&
            Object.keys(usageDetails.procedure).length > 0 &&
            Object.keys(procedureInfoData).length > 0 &&
            !levelDataModified
        ) {
            const draftProcedureData = modifyDraftProcedureDataResponse(
                JSON.parse(JSON.stringify(procedureInfoData)),
                usageDetails.procedure
            );

            if (usageDetails.procedure?.otherRetractor?.length > 0) {
                const { otherRetractor } = usageDetails.procedure;
                dispatch(setRetractorUsedOtherField(otherRetractor));
            }

            if (usageDetails?.procedure?.additionProcedure) {
                const mazorCaseCoverage =
                    usageDetails.procedure?.additionProcedure?.mazorCaseCoverage;
                const selectedNewMazorCaseCoverage =
                    mazorCaseCoverageEmployees.filter((item) => {
                        return (
                            item?.caseCoverageFormattedName === mazorCaseCoverage
                        );
                    });

                dispatch(
                    setMazorCaseCoverageValue(selectedNewMazorCaseCoverage[0])
                );
                setSelectedMazorCaseCoverage(selectedNewMazorCaseCoverage[0]);
                setMazorCaseCoverageInputValue(
                    selectedNewMazorCaseCoverage?.[0]?.caseCoverageFormattedName
                );
            }
            if (usageDetails?.procedure?.additionProcedure) {
                const stealthCaseCoverage =
                    usageDetails.procedure?.additionProcedure?.stealthAxisCaseCoverage;
                const selectedNewStealthCaseCoverage =
                    mazorCaseCoverageEmployees.filter((item) => {
                        return (
                            item?.caseCoverageFormattedName === stealthCaseCoverage
                        );
                    });

                if (selectedNewStealthCaseCoverage) {
                    dispatch(
                        setStealthCaseCoverageValue(
                            selectedNewStealthCaseCoverage[0]
                        )
                    );
                    setSelectedStealthAxisCaseCoverage(
                        selectedNewStealthCaseCoverage[0]
                    );
                    setStealthAxisCaseCoverageInputValue(
                        selectedNewStealthCaseCoverage?.[0]?.caseCoverageFormattedName
                    );
                }
            }

            if (
                usageDetails.procedure?.usedUIV?.length > 0 ||
                usageDetails.procedure?.usedLIV?.length > 0
            ) {
                const normalizeLevelCode = (value) => {
                    if (value === null || value === undefined) return '';
                    if (typeof value === 'number')
                        return String(value).padStart(2, '0');
                    const str = String(value).trim();
                    return /^\d+$/.test(str) && str.length === 1
                        ? str.padStart(2, '0')
                        : str;
                };

                const usedUIV = Array.isArray(usageDetails.procedure?.usedUIV)
                    ? usageDetails.procedure.usedUIV
                    : [];
                const usedLIV = Array.isArray(usageDetails.procedure?.usedLIV)
                    ? usageDetails.procedure.usedLIV
                    : [];

                const combinedArray = usedUIV
                    .map((uiv, index) => ({
                        uiv: normalizeLevelCode(uiv),
                        liv: normalizeLevelCode(usedLIV[index]),
                    }))
                    .filter((pair) => pair.uiv && pair.liv);

                const firstPairIdx = combinedArray.findIndex(
                    (pair) => pair.uiv !== '00' && pair.liv !== '00'
                );

                if (firstPairIdx >= 0 && draftProcedureData?.levels?.length > 0) {
                    const firstPair = combinedArray[firstPairIdx];

                    // PrimeReact Dropdown (used by our Select) matches selected value to
                    // options by object reference unless optionValue is provided.
                    // In this screen the options list is `procedureInfoData.levels`,
                    // so we MUST reuse the same object instances from `draftProcedureData.levels`.
                    const mapLevelToSelectOption = (code) => {
                        const match = draftProcedureData.levels.find((lvl) => {
                            if (!lvl || typeof lvl !== 'object') return false;
                            return normalizeLevelCode(lvl.code) === code;
                        });
                        return match || null;
                    };

                    const firstUivOption = mapLevelToSelectOption(firstPair.uiv);
                    const firstLivOption = mapLevelToSelectOption(firstPair.liv);

                    if (firstUivOption) dispatch(setUivDropdownValue(firstUivOption));
                    if (firstLivOption) dispatch(setLivDropdownValue(firstLivOption));

                    const remainingPairs = combinedArray.filter(
                        (_pair, idx) => idx !== firstPairIdx
                    );

                    remainingPairs.forEach((pair, index) => {
                        const usedUivValue = mapLevelToSelectOption(pair.uiv);
                        const usedLivValue = mapLevelToSelectOption(pair.liv);
                        const newLevel = {
                            id: index + 1,
                            uivDropdown: {
                                uivState: usedUivValue,
                                isSelected: true,
                            },
                            livDropdown: {
                                livState: usedLivValue,
                                isSelected: true,
                            },
                            error: '',
                            informationalMsg: '',
                        };
                        dispatch(addProcedureLevel(newLevel));
                    });
                }
            }

            setProcedureInfoData(draftProcedureData);
            dispatch(setProcedureData(draftProcedureData));
            let reportUsageObj = { ...reportUsageFormObj };
            if (
                Object.keys(createProcedureDraftOrderObject(draftProcedureData))
                    .length > 0
            ) {
                reportUsageObj.procedure = createProcedureDraftOrderObject(
                    draftProcedureData,
                    '',
                    '',
                    uivArray,
                    livArray,
                    totalLevelsNumber
                );
            }
            setReportUsageFormObj((prevStateObj) => ({
                ...prevStateObj,
                ...reportUsageObj,
            }));
            setHasPopulatedProcedure(true);
        }
    }, [procedureInfoData, usageDetails, hasPopulatedProcedure, levelDataModified]);
};